/**
 * **复制 / 粘贴** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 剪贴板本身是纯数据（`graph/nodeClipboard.ts` 的 `copyNodes` /
 * `pasteNodes` 做深拷贝与 id 重写），这里维护的是**宿主的两件状态**：
 *
 *   `clipboard`   当前剪贴板内容（`undefined` = 没复制过）
 *   `pasteRound`  粘贴轮次 —— 连续粘贴逐次错开，不会叠在一起
 *
 * 外加给菜单用的 `clipboardState`（`canCopy` / `canPaste`）。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * 宿主作用域的东西由 `ctx` 注入。注意 `selectedIds` 是**闭包函数**而不是
 * ref —— 它在 Playground 里声明在这段之后，传函数就能保持「调用时才读」。
 */
import { computed, nextTick, ref } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge, FlowNode as Node } from '@/graph/flowTypes'
import { copyNodes, pasteNodes } from '@/graph/nodeClipboard'
import type { ClipboardPayload } from '@/graph/nodeClipboard'
import { republish } from '@/graph/documentSync'
import { selectOnly } from '@/graph/flowStore'
import type { FlowStoreApi } from '@/graph/flowStore'
import type { GraphNodeModel } from '@/types/node'

/**
 * 节点 `data` 的形状 —— 只描述这个 composable 用得到的部分。
 *
 * ⚠️ 本地定义，不从 Playground 导 —— composable 不该反向依赖宿主文件。
 */
interface NpNodeData {
  model: GraphNodeModel
  collapsed: boolean
}

export interface ClipboardCtx {
  nodes: { value: Node[] }
  edges: { value: Edge[] }
  flowStore: { value: FlowStoreApi | null }
  /** 当前选中的节点 id（`doCopy` 只看它，声明在宿主更靠后的位置） */
  selectedIds: () => string[]
  /** 记历史（`doPaste` 粘贴前记一份），来自 useUndoRedo */
  commit: (label: string, coalesceMs?: number) => void
  showNotice: (message: string) => void
}

export function useClipboard(ctx: ClipboardCtx) {
  const { nodes, edges, flowStore, selectedIds, commit, showNotice } = ctx

const clipboard = ref<ClipboardPayload | null>(null)
/** 粘贴轮次：连续粘贴逐次错开，不会叠在一起 */
let pasteRound = 0

const clipboardState = computed(() => {
  void clipboard.value
  return {
    canCopy: selectedIds().length > 0,
    canPaste: (clipboard.value?.nodes.length ?? 0) > 0,
  }
})

function doCopy() {
  const ids = selectedIds()
  if (ids.length === 0) {
    showNotice('没有选中的节点')
    return
  }
  const payload = copyNodes(nodes.value, edges.value, ids)
  clipboard.value = payload
  pasteRound = 0
  showNotice(
    payload.edges.length > 0
      ? `已复制 ${payload.nodes.length} 个节点、${payload.edges.length} 条连线`
      : `已复制 ${payload.nodes.length} 个节点`,
  )
}

function doPaste() {
  const payload = clipboard.value
  if (!payload || payload.nodes.length === 0) {
    showNotice('剪贴板是空的')
    return
  }
  commit('粘贴节点')
  pasteRound += 1

  const result = pasteNodes(payload, {
    makeId: (old, index) => `${old}-c${pasteRound}-${Date.now().toString(36)}${index}`,
    offset: { x: 28 * pasteRound, y: 28 * pasteRound },
    /*
     * 模型里也存了一份 id，必须一起改 ——
     * 只改 node.id 会让「按 id 查模型」全部失效（端口、宽度、折叠态全丢）。
     */
    rewriteData: (data, newId) => {
      const d = data as NpNodeData
      return { ...d, model: { ...d.model, id: newId } }
    },
  })

  type Loose = Record<string, unknown>
  const addedNodes = result.nodes as unknown as Node[]
  // 走最小结构类型拼接 —— Vue Flow 的 Node/Edge 泛型太深，展开会触发 TS2589
  const currentNodes = nodes.value as unknown as Loose[]
  const currentEdges = edges.value as unknown as Loose[]
  nodes.value = [...currentNodes, ...(result.nodes as Loose[])] as unknown as Node[]
  edges.value = [...currentEdges, ...(result.edges as Loose[])] as unknown as Edge[]

  nextTick(() => {
    flowStore.value?.updateNodeInternals()
    /*
     * 粘贴完选中新的那批 —— 否则用户还得再框一次。
     *
     * ⚠️ 而且必须是**替换**（`selectOnly`）：追加的话「选中 A → 粘贴」
     * 会把 A 留在选中集里，接着拖一下就连 A 一起拖走了。
     */
    const dirty = selectOnly(
      nodes.value as unknown as { id: string; selected?: boolean }[],
      addedNodes.map((node) => node.id),
    )
    nodes.value = republish(nodes.value, dirty)
  })
  showNotice(`已粘贴 ${addedNodes.length} 个节点`)
}

  return { clipboardState, doCopy, doPaste }
}
